import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatSnackBar } from '@angular/material';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';

interface VersionRow { id?: number; modem_type: string; version_esperada: string; activo: number; }
interface PerfilRow  { id?: number; modem_type: string; line_profile: string; vas_profile: string; service_profile: string; activo: number; }
interface OpticaRow  { id?: number; item: string; min_dbm: number; max_dbm: number; units: string; }
interface VlanRow    {
  id?: number; modem_type: string;
  service_description?: string; vlan_choice?: string; tag_transform?: string;
  order_vlan_id?: string; inner_vlan_id?: string;
  interface_selection: string; gemportid: number;
  service_type?: string; user_vlan: number;
  traffic_profile_tx: string; traffic_profile_rx: string;
  activo: number;
}
interface MonitorCfg { interval_sec: number; batch_size: number; }

@Component({
  selector: 'app-configuracion-certificados',
  templateUrl: './configuracion-certificados.component.html',
  styleUrls: ['./configuracion-certificados.component.scss']
})
export class ConfiguracionCertificadosComponent implements OnInit {

  // Permisos
  public puedeVer = false;
  public puedeCrear = false;
  public puedeEditar = false;
  public puedeBorrar = false;

  // Datos por tabla
  public versiones: VersionRow[] = [];
  public perfiles: PerfilRow[] = [];
  public optica:   OpticaRow[]  = [];
  public vlans:    VlanRow[]    = [];

  // Loading flags
  public loading = { versiones: false, perfiles: false, optica: false, vlans: false, monitorCfg: false };

  // Formularios (modelos en edicion). null = nada en edicion.
  public formVersion: VersionRow | null = null;
  public formPerfil:  PerfilRow  | null = null;
  public formOptica:  OpticaRow  | null = null;
  public formVlan:    VlanRow    | null = null;

  // Monitor en tiempo real (singleton, no es lista)
  public monitorCfg: MonitorCfg | null = null;
  // Toggle para ocultar el tab del configurador del monitor sin borrar el codigo.
  public mostrarMonitorCfg: boolean = false;

  constructor(
    private gateway: GatewayService,
    private localSave: LocalSaveService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit() {
    const user = this.localSave.get('usuarioPGV');
    this.puedeVer    = this._tienePermiso('CONFIG_CERT', 'ver')    || (user && Number(user.idrol) === 10);
    if (!this.puedeVer) { this.router.navigate(['/home']); return; }
    this.puedeCrear  = this._tienePermiso('CONFIG_CERT', 'crear')  || (user && Number(user.idrol) === 10);
    this.puedeEditar = this._tienePermiso('CONFIG_CERT', 'editar') || (user && Number(user.idrol) === 10);
    this.puedeBorrar = this._tienePermiso('CONFIG_CERT', 'borrar') || (user && Number(user.idrol) === 10);

    this.loadAll();
  }

  private _tienePermiso(modulo: string, tipo: string = 'ver'): boolean {
    const user = this.localSave.get('usuarioPGV');
    if (!user || !user.permisos) return false;
    const perm = user.permisos.find(p => p.modulo === modulo);
    if (!perm) return false;
    return perm['permiso_' + tipo] === 1;
  }

  // -----------------------------------------------------------
  // Helpers genericos
  // -----------------------------------------------------------
  private call(path: string, body: any = {}): Promise<any> {
    const url = this.gateway.globalip + path;
    const headers = { 'Authorization': 'p ' + this.localSave.get('OSR_TKN') };
    return this.gateway.http.post(url, body, { headers }).toPromise();
  }

  private snack(msg: string) { this.snackBar.open(msg, 'Cerrar', { duration: 2500 }); }

  public async loadAll() {
    await Promise.all([this.loadVersiones(), this.loadPerfiles(), this.loadOptica(), this.loadVlans(), this.loadMonitorCfg()]);
  }

  // -----------------------------------------------------------
  // MONITOR EN TIEMPO REAL
  // -----------------------------------------------------------
  public async loadMonitorCfg() {
    this.loading.monitorCfg = true;
    try {
      const r: any = await this.call('/api/PGV/CONFIG_CERT/MONITOR_CFG/LIST');
      this.monitorCfg = (r && r.config) || { interval_sec: 30, batch_size: 10 };
    } catch (e) { this.snack('Error cargando configuración del monitor'); }
    this.loading.monitorCfg = false;
  }
  public async saveMonitorCfg() {
    if (!this.monitorCfg) return;
    const iv = Number(this.monitorCfg.interval_sec);
    const bs = Number(this.monitorCfg.batch_size);
    if (!isFinite(iv) || iv < 1) { this.snack('Intervalo inválido (>= 1)'); return; }
    if (!isFinite(bs) || bs < 1) { this.snack('Tamaño de lote inválido (>= 1)'); return; }
    try {
      await this.call('/api/PGV/CONFIG_CERT/MONITOR_CFG/MODIFICAR', { interval_sec: iv, batch_size: bs });
      this.snack('Configuración actualizada');
      this.loadMonitorCfg();
    } catch (e) { this.snack('Error guardando'); }
  }

  // -----------------------------------------------------------
  // VERSIONES
  // -----------------------------------------------------------
  public async loadVersiones() {
    this.loading.versiones = true;
    try {
      const r: any = await this.call('/api/PGV/CONFIG_CERT/VERSIONES/LIST');
      this.versiones = (r && r.rows) || [];
    } catch (e) { this.snack('Error cargando versiones'); }
    this.loading.versiones = false;
  }
  public newVersion() {
    if (!this.puedeCrear) return;
    this.formVersion = { modem_type: '', version_esperada: '', activo: 1 };
  }
  public editVersion(row: VersionRow) {
    if (!this.puedeEditar) return;
    this.formVersion = { ...row };
  }
  public cancelVersion() { this.formVersion = null; }
  public async saveVersion() {
    if (!this.formVersion) return;
    if (!this.formVersion.modem_type || !this.formVersion.version_esperada) { this.snack('Modem y versión son obligatorios'); return; }
    const isNew = !this.formVersion.id;
    const path = isNew ? '/api/PGV/CONFIG_CERT/VERSIONES/CREAR' : '/api/PGV/CONFIG_CERT/VERSIONES/MODIFICAR';
    try {
      await this.call(path, this.formVersion);
      this.snack(isNew ? 'Versión creada' : 'Versión actualizada');
      this.formVersion = null;
      this.loadVersiones();
    } catch (e) { this.snack('Error guardando'); }
  }
  public async deleteVersion(row: VersionRow) {
    if (!this.puedeBorrar || !row.id) return;
    if (!confirm('¿Eliminar la versión "' + row.version_esperada + '" de ' + row.modem_type + '?')) return;
    try {
      await this.call('/api/PGV/CONFIG_CERT/VERSIONES/BORRAR', { id: row.id });
      this.snack('Versión eliminada');
      this.loadVersiones();
    } catch (e) { this.snack('Error eliminando'); }
  }

  // -----------------------------------------------------------
  // PERFILES
  // -----------------------------------------------------------
  public async loadPerfiles() {
    this.loading.perfiles = true;
    try {
      const r: any = await this.call('/api/PGV/CONFIG_CERT/PERFILES/LIST');
      this.perfiles = (r && r.rows) || [];
    } catch (e) { this.snack('Error cargando perfiles'); }
    this.loading.perfiles = false;
  }
  public newPerfil() {
    if (!this.puedeCrear) return;
    this.formPerfil = { modem_type: '', line_profile: '', vas_profile: '', service_profile: '', activo: 1 };
  }
  public editPerfil(row: PerfilRow) {
    if (!this.puedeEditar) return;
    this.formPerfil = { ...row };
  }
  public cancelPerfil() { this.formPerfil = null; }
  public async savePerfil() {
    if (!this.formPerfil) return;
    if (!this.formPerfil.modem_type || !this.formPerfil.line_profile || !this.formPerfil.vas_profile || !this.formPerfil.service_profile) {
      this.snack('Todos los campos son obligatorios'); return;
    }
    const isNew = !this.formPerfil.id;
    const path = isNew ? '/api/PGV/CONFIG_CERT/PERFILES/CREAR' : '/api/PGV/CONFIG_CERT/PERFILES/MODIFICAR';
    try {
      await this.call(path, this.formPerfil);
      this.snack(isNew ? 'Perfil creado' : 'Perfil actualizado');
      this.formPerfil = null;
      this.loadPerfiles();
    } catch (e) { this.snack('Error guardando'); }
  }
  public async deletePerfil(row: PerfilRow) {
    if (!this.puedeBorrar || !row.id) return;
    if (!confirm('¿Eliminar perfil de ' + row.modem_type + ' (' + row.service_profile + ')?')) return;
    try {
      await this.call('/api/PGV/CONFIG_CERT/PERFILES/BORRAR', { id: row.id });
      this.snack('Perfil eliminado');
      this.loadPerfiles();
    } catch (e) { this.snack('Error eliminando'); }
  }

  // -----------------------------------------------------------
  // OPTICA
  // -----------------------------------------------------------
  public async loadOptica() {
    this.loading.optica = true;
    try {
      const r: any = await this.call('/api/PGV/CONFIG_CERT/OPTICA/LIST');
      this.optica = (r && r.rows) || [];
    } catch (e) { this.snack('Error cargando potencia óptica'); }
    this.loading.optica = false;
  }
  public newOptica() {
    if (!this.puedeCrear) return;
    this.formOptica = { item: '', min_dbm: 0, max_dbm: 0, units: 'dBm' };
  }
  public editOptica(row: OpticaRow) {
    if (!this.puedeEditar) return;
    this.formOptica = { ...row };
  }
  public cancelOptica() { this.formOptica = null; }
  public async saveOptica() {
    if (!this.formOptica) return;
    if (!this.formOptica.item || this.formOptica.min_dbm == null || this.formOptica.max_dbm == null) {
      this.snack('Item, min y max son obligatorios'); return;
    }
    const isNew = !this.formOptica.id;
    const path = isNew ? '/api/PGV/CONFIG_CERT/OPTICA/CREAR' : '/api/PGV/CONFIG_CERT/OPTICA/MODIFICAR';
    try {
      await this.call(path, this.formOptica);
      this.snack(isNew ? 'Rango creado' : 'Rango actualizado');
      this.formOptica = null;
      this.loadOptica();
    } catch (e) { this.snack('Error guardando'); }
  }
  public async deleteOptica(row: OpticaRow) {
    if (!this.puedeBorrar || !row.id) return;
    if (!confirm('¿Eliminar el rango "' + row.item + '"?')) return;
    try {
      await this.call('/api/PGV/CONFIG_CERT/OPTICA/BORRAR', { id: row.id });
      this.snack('Rango eliminado');
      this.loadOptica();
    } catch (e) { this.snack('Error eliminando'); }
  }

  // -----------------------------------------------------------
  // VLANS
  // -----------------------------------------------------------
  public async loadVlans() {
    this.loading.vlans = true;
    try {
      const r: any = await this.call('/api/PGV/CONFIG_CERT/VLANS/LIST');
      this.vlans = (r && r.rows) || [];
    } catch (e) { this.snack('Error cargando VLANs'); }
    this.loading.vlans = false;
  }
  public newVlan() {
    if (!this.puedeCrear) return;
    this.formVlan = {
      modem_type: '', service_description: '', vlan_choice: '', tag_transform: '',
      order_vlan_id: '', inner_vlan_id: '',
      interface_selection: 'Gem Port 1', gemportid: 1,
      service_type: 'Multiservice VLAN', user_vlan: 0,
      traffic_profile_tx: '', traffic_profile_rx: '',
      activo: 1
    };
  }
  public editVlan(row: VlanRow) {
    if (!this.puedeEditar) return;
    this.formVlan = { ...row };
  }
  public cancelVlan() { this.formVlan = null; }
  public async saveVlan() {
    if (!this.formVlan) return;
    if (!this.formVlan.modem_type || !this.formVlan.interface_selection || this.formVlan.gemportid == null
        || this.formVlan.user_vlan == null || !this.formVlan.traffic_profile_tx || !this.formVlan.traffic_profile_rx) {
      this.snack('Modem, interface, gemportid, user_vlan, traffic TX y traffic RX son obligatorios'); return;
    }
    const isNew = !this.formVlan.id;
    const path = isNew ? '/api/PGV/CONFIG_CERT/VLANS/CREAR' : '/api/PGV/CONFIG_CERT/VLANS/MODIFICAR';
    try {
      await this.call(path, this.formVlan);
      this.snack(isNew ? 'VLAN creada' : 'VLAN actualizada');
      this.formVlan = null;
      this.loadVlans();
    } catch (e) { this.snack('Error guardando'); }
  }
  public async deleteVlan(row: VlanRow) {
    if (!this.puedeBorrar || !row.id) return;
    if (!confirm('¿Eliminar VLAN ' + row.modem_type + ' / Gem ' + row.gemportid + ' / uv=' + row.user_vlan + '?')) return;
    try {
      await this.call('/api/PGV/CONFIG_CERT/VLANS/BORRAR', { id: row.id });
      this.snack('VLAN eliminada');
      this.loadVlans();
    } catch (e) { this.snack('Error eliminando'); }
  }
}
